<template>
  <div class="bg-white rounded-lg shadow-md p-6">
    <div class="flex items-center justify-between mb-4">
      <h3 class="text-lg font-semibold text-gray-700">
        ⚠️ Pedidos Retrasados ({{ retrasos.length }})
      </h3>
      <button 
        @click="exportarCSV"
        class="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
      >
        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
        </svg>
        Exportar CSV
      </button>
    </div>

    <!-- Tabla responsive -->
    <div class="overflow-x-auto">
      <table class="min-w-full divide-y divide-gray-200">
        <thead class="bg-gray-50">
          <tr>
            <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Folio
            </th>
            <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Cliente
            </th>
            <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Fecha Entrada
            </th>
            <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Fecha Entrega
            </th>
            <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Días Transcurridos
            </th>
            <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Límite
            </th>
            <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Retraso
            </th>
            <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Estado
            </th>
          </tr>
        </thead>
        <tbody class="bg-white divide-y divide-gray-200">
          <tr 
            v-for="(retraso, index) in retrasos" 
            :key="index"
            class="hover:bg-gray-50 transition-colors"
          >
            <td class="px-6 py-4 whitespace-nowrap">
              <div class="text-sm font-medium text-gray-900">{{ retraso.folio }}</div>
            </td>
            <td class="px-6 py-4">
              <div class="text-sm text-gray-900">{{ retraso.cliente }}</div>
            </td>
            <td class="px-6 py-4 whitespace-nowrap">
              <div class="text-sm text-gray-500">{{ formatearFecha(retraso.fecha_entrada) }}</div>
            </td>
            <td class="px-6 py-4 whitespace-nowrap">
              <div class="text-sm text-gray-500">{{ formatearFecha(retraso.fecha_entrega) }}</div>
            </td>
            <td class="px-6 py-4 whitespace-nowrap">
              <div class="text-sm text-gray-900 font-semibold">
                {{ retraso.dias_habiles_transcurridos }} días
              </div>
            </td>
            <td class="px-6 py-4 whitespace-nowrap">
              <div class="text-sm text-gray-500">{{ retraso.limite_dias }} días</div>
            </td>
            <td class="px-6 py-4 whitespace-nowrap">
              <span class="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-red-100 text-red-800">
                +{{ retraso.dias_retraso }} días
              </span>
            </td>
            <td class="px-6 py-4 whitespace-nowrap">
              <span 
                :class="retraso.estado === 'RETRASADO' ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'"
                class="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium"
              >
                {{ retraso.estado }}
              </span>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Mensaje si no hay retrasos -->
    <div v-if="retrasos.length === 0" class="text-center py-12">
      <svg class="mx-auto h-12 w-12 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
      </svg>
      <h3 class="mt-2 text-sm font-medium text-gray-900">¡Excelente trabajo!</h3>
      <p class="mt-1 text-sm text-gray-500">No hay pedidos retrasados en este período.</p>
    </div>
  </div>
</template>

<script>
export default {
  name: 'DetalleRetrasos',
  props: {
    retrasos: {
      type: Array,
      default: () => [],
    },
  },
  setup(props) {
    const formatearFecha = (fecha) => {
      if (!fecha) return '-';
      const date = new Date(fecha);
      return date.toLocaleDateString('es-MX', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    };

    const exportarCSV = () => {
      if (props.retrasos.length === 0) return;

      const headers = [
        'Folio',
        'Cliente',
        'Fecha Entrada',
        'Fecha Entrega',
        'Días Transcurridos',
        'Límite Días',
        'Días Retraso',
        'Estado',
      ];

      const rows = props.retrasos.map(r => [
        r.folio,
        r.cliente,
        r.fecha_entrada,
        r.fecha_entrega,
        r.dias_habiles_transcurridos,
        r.limite_dias,
        r.dias_retraso,
        r.estado,
      ]);

      const csv = [
        headers.join(','),
        ...rows.map(row => row.join(','))
      ].join('\n');

      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      
      link.setAttribute('href', url);
      link.setAttribute('download', `retrasos_${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    };

    return {
      formatearFecha,
      exportarCSV,
    };
  },
};
</script>
